import React from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
import { PageLightbox } from './PageLightbox';
import { seasonShowPages } from '../data/seasonShowPages';
import { useCoverflow } from '../hooks/useCoverflow';
const ease = [0.23, 1, 0.32, 1] as const;
const levels = [{
  label: 'L1 Beginner',
  className: 'bg-mint/20 text-mint-deep'
}, {
  label: 'L2 Developing',
  className: 'bg-amber/25 text-amber-deep'
}, {
  label: 'L3 Advanced',
  className: 'bg-coral/15 text-coral-deep'
}];
const CAPTION_SPACE = 56;
export function SeasonShowCarousel() {
  const c = useCoverflow(seasonShowPages.length);
  const stageHeight = c.slideWidth * 1.25 + CAPTION_SPACE;
  return <section aria-labelledby="season-show-heading" className="w-full overflow-hidden bg-gradient-to-b from-glacier-soft via-white to-lilac-soft text-ink" data-id="element-392" data-mp="1iywjy24x8aqm">
      <div className="max-w-6xl mx-auto px-5 py-16 sm:py-20" data-id="element-393" data-mp="qbsiu88y8gk4">
        <header className="mx-auto max-w-2xl text-center" data-id="element-394" data-mp="3l4i078byrf84">
          <p className="text-[11px] font-semibold uppercase tracking-[1.8px] text-glacier-deep" data-id="element-395" data-mp="3l1vs97l5uxpd">
            Look inside
          </p>
          <h2 id="season-show-heading" className="mt-3 font-display text-3xl font-bold tracking-tight text-ink sm:text-5xl" data-id="element-396" data-mp="3ut9nh362xbcp">
            One number. Every level shines.
          </h2>
          <p className="mt-4 text-base text-ink-soft sm:text-lg" data-id="element-397" data-mp="3265q9kd8a1bh">
            55 pages, 8 weeks, 1 complete show number for up to 30 skaters —
            from theme to final bow.
          </p>
          <ul className="mt-5 flex flex-wrap justify-center gap-2" data-id="element-398" data-mp="3b5ixe6dvqdt1">
            {levels.map(level => <li key={level.label} className={`rounded-full px-3 py-1 text-xs font-semibold ${level.className}`} data-id="element-399" data-mp="3g5y96tiijd8w">
                {level.label}
              </li>)}
          </ul>
        </header>

        <div role="region" aria-roledescription="carousel" aria-label="Vol. 5 — The Season Show, sample pages" className="mt-10 sm:mt-12" onMouseEnter={() => c.setHovered(true)} onMouseLeave={() => c.setHovered(false)} onFocus={() => c.setFocused(true)} onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          c.setFocused(false);
        }
      }} data-id="element-400" data-mp="2rhss08qfistf">
          <motion.div ref={c.stageRef} tabIndex={0} onKeyDown={c.onKeyDown} onPanStart={c.onPanStart} onPanEnd={c.onPanEnd} className="relative w-full select-none rounded-2xl focus:outline-none focus-visible:ring-4 focus-visible:ring-glacier-deep/40" style={{
          height: stageHeight,
          touchAction: 'pan-y'
        }} data-id="element-401" data-mp="28oyrgjtjz38q">
            {c.slideWidth > 0 && seasonShowPages.map((page, i) => {
            const d = c.offsetOf(i);
            const abs = Math.abs(d);
            const active = d === 0;
            const visible = abs <= 1;
            const scale = active ? 1 : c.isDesktop ? 0.85 : 0.92;
            const opacity = active ? 1 : visible ? 0.55 : 0;
            return <motion.div key={page.src} role="group" aria-roledescription="slide" aria-label={`${i + 1} of ${seasonShowPages.length}`} aria-hidden={!visible} className="absolute left-1/2 top-0" style={{
              width: c.slideWidth,
              marginLeft: -c.slideWidth / 2,
              zIndex: 10 - abs,
              transformOrigin: 'top center',
              pointerEvents: visible ? 'auto' : 'none'
            }} initial={false} animate={{
              x: d * c.spacing,
              scale,
              opacity
            }} transition={c.reduceMotion ? {
              duration: 0
            } : {
              duration: 0.3,
              ease
            }} data-id="element-402" data-mp="3rpzc9k0thm17" data-mpi={i}>
                    <figure data-id="element-403" data-mp="35jopt8ryeyzo">
                      <button type="button" tabIndex={visible ? 0 : -1} aria-label={active ? `Open full size: ${page.caption}` : `Show slide: ${page.caption}`} onClick={() => {
                  if (c.consumePan()) return;
                  if (active) c.setLightbox(i);else c.setIndex(i);
                }} className={`block w-full overflow-hidden rounded-2xl bg-white shadow-card ring-1 ring-rink/5 focus:outline-none focus-visible:ring-4 focus-visible:ring-amber/60 ${active ? 'cursor-zoom-in' : 'cursor-pointer'}`} data-id="element-404" data-mp="35iwu7xycx19e">
                        <img src={page.src} alt={page.caption} width={1200} height={1500} draggable={false} loading={i < 2 ? 'eager' : 'lazy'} className="aspect-[4/5] w-full object-contain" data-id="element-405" data-mp="tahtsfkef4ap" />
                      </button>
                      <figcaption className="mt-3 px-2 text-center text-[13px] leading-snug text-ink-muted" data-id="element-406" data-mp="1by3bpv8abgq7">
                        {page.caption}
                      </figcaption>
                    </figure>
                  </motion.div>;
          })}
          </motion.div>

          <div className="mt-6 flex items-center justify-center gap-5" data-id="element-407" data-mp="3is72c3akwmrj">
            <button type="button" onClick={c.prev} aria-label="Previous page" className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-rink shadow-card ring-1 ring-glacier-dark transition-colors duration-150 hover:bg-glacier-soft focus:outline-none focus-visible:ring-4 focus-visible:ring-glacier-deep/40" data-id="element-408" data-mp="3mt0oos8dhtib">
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-409" data-mp="yvirw4fzdfi8" />
            </button>

            <div className="flex items-center gap-2" data-id="element-410" data-mp="1cvbh1vbuzno3">
              {seasonShowPages.map((page, i) => <button key={page.src} type="button" onClick={() => c.setIndex(i)} aria-label={`Go to page ${i + 1}: ${page.caption}`} aria-current={c.index === i} className="flex h-6 items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-glacier-deep rounded-full" data-id="element-411" data-mp="17w8jsx24elqo" data-mpi={i}>
                  <span className={`block h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${c.index === i ? 'w-6 bg-rink' : 'w-2 bg-glacier-dark'}`} data-id="element-412" data-mp="1qp2uvfwmhtpm" />
                </button>)}
            </div>

            <button type="button" onClick={c.next} aria-label="Next page" className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-rink shadow-card ring-1 ring-glacier-dark transition-colors duration-150 hover:bg-glacier-soft focus:outline-none focus-visible:ring-4 focus-visible:ring-glacier-deep/40" data-id="element-413" data-mp="218ljfwsthrj7">
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-414" data-mp="1hmpa024uvzxt" />
            </button>
          </div>
        </div>

        <div className="mx-auto mt-10 max-w-sm" data-id="element-415" data-mp="1mhmkldhsahct">
          <CtaButton data-id="element-416" data-mp="18liqrlatob0v">Get The Season Show</CtaButton>
        </div>
      </div>

      <PageLightbox pages={seasonShowPages} index={c.lightbox} onClose={() => c.setLightbox(null)} onChange={i => {
      c.setLightbox(i);
      c.setIndex(i);
    }} data-id="element-417" data-mp="1djspu6zo3vrr" />
    </section>;
}